import AddToCartButton from '../add-to-cart-button'
import { ProductTypeProps, Maybe } from '.'
import { Card } from './product-card-gallery-composable'
import { handeOnClickProduct } from './utils'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { ProductCardGallerySwitch } from '../cards'
import classNames from 'classnames'
import { DigitalDataOrigin } from '../utils/digital-data-types'
import GoToProductButton from '../go-to-product-button/go-to-product-button'
import ProductCardImage from '../product-card-image'
import type { Props as HeadingProps } from '@dustin-web/microsite-components/src/heading'

type Props = {
  imagePriority?: boolean
  product?: Maybe<ProductTypeProps>
  ticket: string
  mainProductErpIdentifier?: string
  className?: string
  slim?: boolean
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex?: number
  isProductAccessories?: boolean
  showSideButtons?: boolean
  preventSetLastUpdatedProductErpIdentifier?: boolean
  hTag?: HeadingProps['hTag']
}

const ProductCardExtended = ({
  product,
  ticket,
  imagePriority = false,
  mainProductErpIdentifier,
  className = '',
  slim = false,
  digitalDataOrigin,
  digitalIndex = 0,
  isProductAccessories = false,
  showSideButtons,
  preventSetLastUpdatedProductErpIdentifier,
  hTag,
}: Props) => {
  const { translate } = useTranslations()
  const { isSmallScreen } = useResponsive()

  if (!product) {
    return null
  }

  const { availability } = product

  const onClickProduct = () => {
    handeOnClickProduct(digitalDataOrigin, product as any, digitalIndex)
  }

  if (isSmallScreen) {
    return (
      <>
        <ProductCardGallerySwitch
          product={product}
          ticket={ticket}
          imagePriority={imagePriority}
          inFilterList={true}
          mainProductErpIdentifier={mainProductErpIdentifier}
          digitalDataOrigin={digitalDataOrigin}
          digitalIndex={digitalIndex}
          isProductAccessories={isProductAccessories}
          showSideButtons={showSideButtons}
          preventSetLastUpdatedProductErpIdentifier={preventSetLastUpdatedProductErpIdentifier}
          hTag={hTag}
        />
      </>
    )
  }

  const showBadge =
    product.availability?.isAvailableForSale &&
    (product.isNewProduct ||
      product.price?.isBestSeller ||
      product.price?.isBid ||
      product.price?.isCampaign ||
      product.price?.isRecommendedProduct)

  const imgWidth = !slim ? 150 : 64
  const imgHeight = !slim ? 150 : 64

  const displayGoToProductButton = product?.minQuantityPerOrder && product.minQuantityPerOrder > 1

  return (
    <Card.Container
      customClassName={`col-12 u-mb-16 ${className}`}
      digitalIndex={digitalIndex}
      digitalDataOrigin={digitalDataOrigin}
      product={product}>
      <div
        className={classNames('c-product-card c-product-card--list t-nullify', {
          'c-product-card--list--slim': slim,
        })}>
        <ProductCardImage
          product={product}
          ticket={ticket}
          onClickProduct={onClickProduct}
          className={classNames('u-pr-16', {
            'u-pt-8': !showBadge,
            'u-pt-24': showBadge,
            'u-pb-8': !(product.energyClass || showBadge),
            'u-pb-24': product.energyClass || showBadge,
          })}
          imageWidth={imgWidth}
          imageHeight={imgHeight}
          priority={imagePriority}
          useAnchorTag={true}
          showEnergyLabel
        />
        <div className="c-product-card__product-info">
          <div className="d-flex flex-column flex-fill u-w-100 u-pr-8">
            <div className="u-mb-8 flex-fill">
              <Card.Title
                product={product}
                ticket={ticket}
                digitalDataIndex={digitalIndex}
                digitalDataOrigin={digitalDataOrigin}
                hTag={hTag}
              />
              <div className="c-product-card-info u-py-8">
                <span className="c-product-card-info__text">
                  {/* Blank space after productid to facilitate selection by doubleclicking in browser */}
                  {translate('ProductID')} {product.id} {}
                </span>
                <span className="c-product-card-info__text">
                  {translate('ManufacturerPartNumber')} {product.manufacturerProductIdentifier}
                </span>
              </div>
              <Card.SpecificationList slim={slim} list={product.promotedSpecifications} />
            </div>
            <Card.ActionRow showActionRow classes="align-items-end">
              <Card.Availability availability={availability} isBid={product.price?.isBid} />
              <Card.Compare
                id={product.id}
                displayName={product.displayName || ''}
                categoryName={product.categoryName}
              />

              <Card.Favorites
                product={product}
                digitalDataOrigin={digitalDataOrigin}
                square={false}
              />
            </Card.ActionRow>
          </div>
          <div className="d-flex flex-column">
            <div className="flex-fill">
              <Card.Stars
                reviewCount={product.reviewCount}
                reviewScore={product.reviewScore}
                className="u-text-right u-mb-8"
              />
              <Card.Badge
                isAvailableForSale={product.availability?.isAvailableForSale}
                price={product.price}
                isNewProduct={product.isNewProduct}
              />
              <Card.Price
                isAvailableForSale={product.availability?.isAvailableForSale}
                price={product.price}
              />
            </div>
            {displayGoToProductButton ? (
              <GoToProductButton
                productErpIdentifier={product.productErpIdentifier}
                nameSlug={product.nameSlug}
              />
            ) : (
              <AddToCartButton
                product={product}
                ticket={ticket}
                addToCartText={translate('AddToCart')}
                isAd={false}
                quantity={1}
                digitalDataIndex={digitalIndex}
                mainProductErpIdentifier={mainProductErpIdentifier ?? null}
                digitalDataOrigin={digitalDataOrigin}
                preventSetLastUpdatedProductErpIdentifier={
                  preventSetLastUpdatedProductErpIdentifier
                }
              />
            )}
          </div>
        </div>
      </div>
    </Card.Container>
  )
}

export default ProductCardExtended
